import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

interface AR { id: string; actor_email?: string; action: string; resource: string; target_id?: string; ip?: string; user_agent?: string; timestamp: string; impersonated?: boolean }

export const MasterAudit: React.FC = () => {
  const [rows, setRows] = useState<AR[]>([]);
  const [filter, setFilter] = useState('');
  useEffect(() => { api.get<AR[]>('/master/audit?limit=500').then((r) => setRows(r.data)); }, []);
  const filtered = useMemo(() => {
    if (!filter) return rows;
    const q = filter.toLowerCase();
    return rows.filter((r) => [r.actor_email, r.action, r.resource, r.target_id, r.ip].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [rows, filter]);
  return (
    <div className="space-y-4 animate-fade-in" data-testid="master-audit">
      <div>
        <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Platform audit log</h2>
        <p className="text-sm text-raptor-navy/60 font-body">Every privileged action across every tenant, in one place. Records are append-only and include the originating IP and browser fingerprint.</p>
      </div>
      <Card>
        <CardHeader><CardTitle className="flex items-center justify-between">Audit records · {rows.length}<Input className="max-w-xs" placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="master-audit-filter" /></CardTitle></CardHeader>
        <CardContent className="p-0 overflow-auto max-h-[70vh]">
          <table className="w-full text-sm font-body">
            <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70 sticky top-0">
              <tr>
                <th className="py-2 px-3 text-left">When</th>
                <th className="py-2 px-3 text-left">Actor</th>
                <th className="py-2 px-3 text-left">Action</th>
                <th className="py-2 px-3 text-left">Resource</th>
                <th className="py-2 px-3 text-left">Target</th>
                <th className="py-2 px-3 text-left">Where from</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border/60 align-top" data-testid={`master-audit-row-${r.id}`}>
                  <td className="py-2 px-3 font-tabular text-xs whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</td>
                  <td className="py-2 px-3">{r.actor_email || '—'} {r.impersonated && <Badge variant="warning" className="ml-1">impersonated</Badge>}</td>
                  <td className="py-2 px-3"><Badge variant="blue">{r.action}</Badge></td>
                  <td className="py-2 px-3">{r.resource}</td>
                  <td className="py-2 px-3 font-tabular text-xs">{r.target_id || '—'}</td>
                  <td className="py-2 px-3 text-xs"><div className="font-tabular">{r.ip || '—'}</div><div className="text-raptor-navy/60" title={r.user_agent || ''}>{(r.user_agent || '').slice(0, 40)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};
